'use client';
import {
  YStack,
  XStack,
  Button,
  H1,
  H4,
  Text,
  FormField,
  SchemaForm,
  useForm,
  Feature,
  Separator,
  Anchor,
  useMedia,
} from '@cxnpl/ui';
import type { UseFormReturn } from 'react-hook-form';
import { z } from 'zod';
import { Trans, useTranslation } from 'react-i18next';
import { useRouter } from 'solito/navigation';
import { FileText, BriefcaseBusiness } from '@cxnpl/ui/icons';
import { useTransition } from 'react';
import branding from '@cxnpl/ui/brand';
import { withTrackScreenView, logEvent } from 'app/analytics';
import { useGetPolicies } from '../../../../api/sops/policies/api';
import i18n from '../../../../i18n/i18n.config';
import { useAppDispatch, useAppSelector } from '../../../../store';
import { selectAuthDetails, setPreferredName, setTermsAndConditionsChecked } from '../../../landing/signup/signupSlice';
import { useSetActiveStep } from '../SignupFormContext';

export const PreferredNameSchema = z.object({
  preferredName: FormField.Text({
    required_error: `${i18n.t('auth.signUpPage.preferredNameRequiredErrorMessage')}`,
  })
    .describe(`${i18n.t('auth.signUpPage.preferredNameInputLabel')}`)
    .min(1, `${i18n.t('auth.signUpPage.preferredNameRequiredErrorMessage')}`)
    .max(30, `${i18n.t('auth.signUpPage.preferredNameMaxLengthErrorMessage')}`),
});

export type PreferredNameFormFields = z.infer<typeof PreferredNameSchema>;

export const SignUpFormSchema = PreferredNameSchema.extend({
  termsAndConditionsStatus: FormField.BooleanCheckbox({
    required_error: 'Please agree to this before continuing',
  }).describe(`${i18n.t('auth.signUpPage.termsAndConditions')}`),
}).superRefine((values, ctx) => {
  if (!values.termsAndConditionsStatus) {
    ctx.addIssue({
      code: z.ZodIssueCode.custom,
      message: 'Please agree to this before continuing',
      path: ['termsAndConditionsStatus'],
    });
  }
});

export type SignUpFormFields = z.infer<typeof SignUpFormSchema>;

export interface SignUpFormProps {
  onSubmit: (values: SignUpFormFields, form: UseFormReturn<SignUpFormFields>) => Promise<void> | void;
}

const createStyledDescription = (points: string[]) => {
  return (
    <YStack marginLeft="$space.xs" rowGap="$space.lg" marginTop="$space.xs">
      {points.map((point) => (
        <Text key={point} variant="bodySmall" color="$foreground/surface-subdued">
          {point}
        </Text>
      ))}
    </YStack>
  );
};

const rowDetails = [
  {
    id: 'row-one',
    title: `${i18n.t('auth.signUpPage.businessInfoRowOneTitle')}`,
    description: `${i18n.t('auth.signUpPage.businessInfoRowOneDescription', { productName: branding.productName })}`,
    icon: <BriefcaseBusiness />,
  },
  {
    id: 'row-two',
    title: i18n.t('auth.signUpPage.businessInfoRowTwoTitle'),
    description: createStyledDescription([
      i18n.t('auth.signUpPage.businessInfoRowTwoDescription.point1'),
      i18n.t('auth.signUpPage.businessInfoRowTwoDescription.point2'),
      i18n.t('auth.signUpPage.businessInfoRowTwoDescription.point3'),
      i18n.t('auth.signUpPage.businessInfoRowTwoDescription.point4'),
      i18n.t('auth.signUpPage.businessInfoRowTwoDescription.point5'),
      i18n.t('auth.signUpPage.businessInfoRowTwoDescription.point6'),
    ]),
    icon: <FileText />,
  },
];

export function SignUpStep() {
  const { t } = useTranslation();
  const [isPending, startTransition] = useTransition();
  const router = useRouter();
  const form = useForm(SignUpFormSchema);
  const dispatch = useAppDispatch();
  const { preferredName, termsAndConditionsChecked } = useAppSelector(selectAuthDetails);
  const media = useMedia();
  const { data: policyData } = useGetPolicies();

  const setActiveStep = useSetActiveStep();

  const onSubmit = () => {
    void logEvent('start_signup');
    startTransition(() => {
      setActiveStep('email');
    });
  };

  return (
    <YStack
      width="100%"
      backgroundColor="$background/surface"
      borderRadius="$radius.2xl"
      gap="$space.xl"
      $tablet={{
        maxWidth: 536,
        flexDirection: 'column',
      }}
      $laptop={{
        maxWidth: 1161,
        flexDirection: 'row',
      }}
    >
      <YStack gap="$space.2xl" $laptop={{ width: '45%' }} padding="$space.2xl" $mobile={{ width: '100%' }}>
        <H1 textAlign="left" color="$foreground/surface-default" paddingTop="$space.xl">
          {t('auth.signUpPage.title', { productName: branding.productName })}
        </H1>
        <YStack>
          <SchemaForm
            form={form}
            formProps={{ disabled: isPending }}
            defaultValues={{
              preferredName: preferredName as string,
              termsAndConditionsStatus: termsAndConditionsChecked,
            }}
            props={{
              preferredName: {
                autoFocus: true,
              },
              termsAndConditionsStatus: {
                outlined: true,
                children: (
                  <Text wordWrap="break-word">
                    <Trans
                      i18nKey="auth.signUpPage.termsAndConditions"
                      components={{
                        termsAndCondition: (
                          <Anchor
                            href={policyData?.termsAndConditions.url}
                            color="$text/primary"
                            // ensures the link opens in a new tab
                            target="_blank"
                          />
                        ),
                        privacy: (
                          <Anchor
                            href={policyData?.privacyPolicy.url}
                            color="$text/primary"
                            // ensures the link opens in a new tab
                            target="_blank"
                          />
                        ),
                      }}
                    >
                      I have read and agree to the terms and conditions
                    </Trans>
                  </Text>
                ),
              },
            }}
            onSubmit={onSubmit}
            schema={SignUpFormSchema}
          />
        </YStack>
        <YStack alignItems="center" gap="$lg">
          <Button
            onPress={() => {
              dispatch(setPreferredName(form.watch('preferredName')));
              dispatch(setTermsAndConditionsChecked(form.watch('termsAndConditionsStatus')));
              void form.handleSubmit(onSubmit)();
            }}
            disabled={isPending}
            mode="primary"
            variant="filled"
            size="lg"
            width="100%"
            loading={isPending}
          >
            {t('common.buttons.continue')}
          </Button>
          <XStack alignItems="center" width="100%">
            <Text marginRight="$space.xl">{t('auth.signUpPage.alreadyRegistered')}</Text>
            <Text
              variant="linkMedium"
              width="max-content"
              link
              color="$button/color/button-primary-text"
              onPress={() => {
                router.push('/login');
              }}
            >
              {t('auth.signUpPage.logInButton')}
            </Text>
          </XStack>
        </YStack>
      </YStack>

      <Separator
        vertical={!!media.laptop}
        borderColor="$border/app-subdued"
        $laptop={{ marginVertical: '$space.3xl' }}
        $mobile={{ marginHorizontal: '$space.xl' }}
      />

      <YStack $laptop={{ width: '45%', padding: '$space.3xl' }} $mobile={{ width: '100%', padding: '$space.2xl' }}>
        <H4 marginBottom="$space.2xl">{t('auth.signUpPage.secondaryTitle')}</H4>

        <YStack gap="$space.2xl">
          {rowDetails.map((row) => (
            <YStack key={row.id} flex={1}>
              <Feature title={row.title} icon={row.icon} description={row.description} />
            </YStack>
          ))}
        </YStack>
      </YStack>
    </YStack>
  );
}

export const SignUpStepWithAnalytics = withTrackScreenView(SignUpStep, {
  pageName: 'SignUpLanding',
});
